import React, { useState, useEffect } from 'react';
import { HardDrive, Trash2, AlertCircle, CheckCircle, Loader, Info, Database } from 'lucide-react';
import { showSuccess, showError, showWarning } from '../../utils/notifications';

const DiskCleanup = () => {
  const [diskUsage, setDiskUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupStats, setCleanupStats] = useState(null);
  const [error, setError] = useState(null);
  const [daysToKeep, setDaysToKeep] = useState(30);
  const [deleteOrphaned, setDeleteOrphaned] = useState(true);
  const [dryRun, setDryRun] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const fetchDiskUsage = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/ops/cleanup/disk-usage`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch disk usage');
      
      const data = await response.json();
      setDiskUsage(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiskUsage();
  }, []);

  const handleCleanup = async (confirmed = false) => {
    // Show confirmation dialog for non-dry-run
    if (!dryRun && !confirmed) {
      setShowConfirmDialog(true);
      return;
    }

    try {
      setCleaning(true);
      setError(null);
      setCleanupStats(null);
      setShowConfirmDialog(false);

      const token = localStorage.getItem('admin_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/ops/cleanup/disk-space`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          days_to_keep: daysToKeep,
          delete_orphaned: deleteOrphaned,
          dry_run: dryRun
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Cleanup failed');
      }
      
      const stats = await response.json();
      setCleanupStats(stats);
      
      // Show success notification
      if (dryRun) {
        showSuccess(`Preview complete: ${stats.screenshots_deleted + stats.videos_deleted} files would be deleted`);
      } else {
        showSuccess(`Cleanup complete! Freed ${formatBytes(stats.space_freed_mb)}`);
      }
      
      // Refresh disk usage after cleanup
      if (!dryRun) {
        setTimeout(() => fetchDiskUsage(), 1000);
      }
    } catch (err) {
      setError(err.message);
      showError(`Cleanup failed: ${err.message}`);
    } finally {
      setCleaning(false);
    }
  };

  const getUsageColor = (percent) => {
    if (percent < 70) return 'bg-green-500';
    if (percent < 85) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const formatBytes = (mb) => {
    if (mb < 1024) return `${mb.toFixed(2)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <HardDrive className="w-8 h-8" />
          Disk Space Cleanup
        </h1>
        <p className="text-gray-600 mt-1">
          Manage disk space by removing old screenshots and videos
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-900">Error</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Success Alert */}
      {cleanupStats && (
        <div className={`${dryRun ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'} border rounded-lg p-4`}>
          <div className="flex items-start gap-3">
            <CheckCircle className={`w-5 h-5 ${dryRun ? 'text-blue-500' : 'text-green-500'} flex-shrink-0 mt-0.5`} />
            <div className="flex-1">
              <h3 className={`font-semibold ${dryRun ? 'text-blue-900' : 'text-green-900'}`}>
                {dryRun ? 'Dry Run Complete (No Files Deleted)' : 'Cleanup Complete!'}
              </h3>
              <div className="mt-2 space-y-1 text-sm">
                <p className={dryRun ? 'text-blue-700' : 'text-green-700'}>
                  Screenshots {dryRun ? 'to delete' : 'deleted'}: <strong>{cleanupStats.screenshots_deleted}</strong>
                </p>
                <p className={dryRun ? 'text-blue-700' : 'text-green-700'}>
                  Videos {dryRun ? 'to delete' : 'deleted'}: <strong>{cleanupStats.videos_deleted}</strong>
                </p>
                {cleanupStats.orphaned_deleted > 0 && (
                  <p className={dryRun ? 'text-blue-700' : 'text-green-700'}>
                    Orphaned files {dryRun ? 'to delete' : 'deleted'}: <strong>{cleanupStats.orphaned_deleted}</strong>
                  </p>
                )}
                <p className={`font-semibold ${dryRun ? 'text-blue-900' : 'text-green-900'}`}>
                  Space {dryRun ? 'to free' : 'freed'}: <strong>{formatBytes(cleanupStats.space_freed_mb)}</strong>
                </p>
                <p className={dryRun ? 'text-blue-600' : 'text-green-600'}>
                  Completed in {cleanupStats.execution_time_seconds}s
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disk Usage Overview */}
      {diskUsage && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Overall Disk Usage */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Overall Disk Usage</h2>
              <HardDrive className="w-5 h-5 text-gray-400" />
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Used / Total</span>
                  <span className="font-semibold">{diskUsage.used_gb.toFixed(2)} GB / {diskUsage.total_gb.toFixed(2)} GB</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div 
                    className={`h-full ${getUsageColor(diskUsage.usage_percent)} transition-all duration-300`}
                    style={{ width: `${diskUsage.usage_percent}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm mt-2">
                  <span className={`font-semibold ${diskUsage.usage_percent > 85 ? 'text-red-600' : diskUsage.usage_percent > 70 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {diskUsage.usage_percent.toFixed(1)}% Used
                  </span>
                  <span className="text-gray-600">{diskUsage.free_gb.toFixed(2)} GB Free</span>
                </div>
              </div>

              {diskUsage.usage_percent > 85 && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-700">
                    <strong>Critical:</strong> Disk usage is very high. Run cleanup immediately.
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Storage Breakdown */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Storage Breakdown</h2>
              <Database className="w-5 h-5 text-gray-400" />
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">Screenshots</p>
                  <p className="text-xs text-gray-500">{diskUsage.screenshots_count.toLocaleString()} files</p>
                </div>
                <span className="text-lg font-semibold text-gray-900">
                  {formatBytes(diskUsage.screenshots_mb)}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">Videos</p>
                  <p className="text-xs text-gray-500">{diskUsage.videos_count.toLocaleString()} files</p>
                </div>
                <span className="text-lg font-semibold text-gray-900">
                  {formatBytes(diskUsage.videos_mb)}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-blue-900">Total Media</p>
                  <p className="text-xs text-blue-600">Screenshots + Videos</p>
                </div>
                <span className="text-lg font-semibold text-blue-900">
                  {formatBytes(diskUsage.screenshots_mb + diskUsage.videos_mb)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cleanup Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Trash2 className="w-5 h-5" />
          Cleanup Configuration
        </h2>

        <div className="space-y-6">
          {/* Days to Keep */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Keep files from last (days)
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={daysToKeep}
              onChange={(e) => setDaysToKeep(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-sm text-gray-500 mt-1">
              Files older than {daysToKeep} days will be deleted
            </p>
          </div>

          {/* Delete Orphaned Files */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="delete-orphaned"
              checked={deleteOrphaned}
              onChange={(e) => setDeleteOrphaned(e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div className="flex-1">
              <label htmlFor="delete-orphaned" className="text-sm font-medium text-gray-900 cursor-pointer">
                Delete orphaned files
              </label>
              <p className="text-sm text-gray-500 mt-1">
                Remove screenshots not associated with any test in the database (safer, only files older than 1 day)
              </p>
            </div>
          </div>

          {/* Dry Run */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="dry-run"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div className="flex-1">
              <label htmlFor="dry-run" className="text-sm font-medium text-gray-900 cursor-pointer">
                Dry run (preview only)
              </label>
              <p className="text-sm text-gray-500 mt-1">
                Show what will be deleted without actually deleting anything
              </p>
            </div>
          </div>

          {/* Info Box */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-semibold mb-1">Recommended Settings:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Keep 30 days for regular maintenance</li>
                <li>Use dry run first to preview changes</li>
                <li>Enable orphaned file deletion for maximum space savings</li>
                <li>Run cleanup when disk usage exceeds 70%</li>
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleCleanup}
              disabled={cleaning}
              className={`flex-1 px-6 py-3 rounded-lg font-semibold text-white transition-colors flex items-center justify-center gap-2 ${
                dryRun 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-red-600 hover:bg-red-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {cleaning ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  {dryRun ? 'Running Preview...' : 'Cleaning...'}
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5" />
                  {dryRun ? 'Preview Cleanup' : 'Run Cleanup'}
                </>
              )}
            </button>
            
            <button
              onClick={fetchDiskUsage}
              disabled={loading}
              className="px-6 py-3 rounded-lg font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <HardDrive className="w-5 h-5" />
                  Refresh
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Confirm Cleanup
                </h3>
                <p className="text-gray-600 mb-4">
                  Are you sure you want to delete files older than <strong>{daysToKeep} days</strong>?
                  {deleteOrphaned && (
                    <span className="block mt-2">
                      This will also remove orphaned files not in the database.
                    </span>
                  )}
                </p>
                <p className="text-sm text-red-600 font-medium">
                  ⚠️ This action cannot be undone!
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleCleanup(true)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                Delete Files
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiskCleanup;
